import React, { useEffect } from "react";
import { FileUpload } from "./FileUpload";
import { DataPreview } from "./DataPreview";
import { ColumnMapping } from "./ColumnMapping";
import { ImportProgress } from "./ImportProgress";
import { ImportResults } from "./ImportResults";
import { useImportStore } from "../../store/useImportStore";
import { Upload, Eye, Settings, Clock, CheckCircle } from "lucide-react";
import { cn } from "../../utils/helpers";

const steps = [
  { id: "upload", name: "Upload File", icon: Upload },
  { id: "preview", name: "Preview Data", icon: Eye },
  { id: "mapping", name: "Column Mapping", icon: Settings },
  { id: "progress", name: "Import Progress", icon: Clock },
  { id: "results", name: "Results", icon: CheckCircle },
];

export const ImportWizard: React.FC = () => {
  const { currentStep, resetImport } = useImportStore();

  useEffect(() => {
    // Reset import state when component mounts
    return () => {
      if (currentStep === "upload") {
        resetImport();
      }
    };
  }, []);

  const renderStepContent = () => {
    switch (currentStep) {
      case "upload":
        return <FileUpload />;
      case "preview":
        return <DataPreview />;
      case "mapping":
        return <ColumnMapping />;
      case "progress":
        return <ImportProgress />;
      case "results":
        return <ImportResults />;
      default:
        return <FileUpload />;
    }
  };

  const getStepStatus = (stepId: string) => {
    const stepIndex = steps.findIndex((s) => s.id === stepId);
    const currentIndex = steps.findIndex((s) => s.id === currentStep);

    if (stepIndex < currentIndex) return "completed";
    if (stepIndex === currentIndex) return "current";
    return "upcoming";
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Steps Header */}
      <div className="mb-8">
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {steps.map((step, stepIdx) => {
              const status = getStepStatus(step.id);
              const Icon = step.icon;

              return (
                <li key={step.id} className="relative flex-1">
                  {/* Connection Line */}
                  {stepIdx < steps.length - 1 && (
                    <div
                      className="absolute top-5 left-1/2 w-full h-0.5 transform"
                      aria-hidden="true"
                    >
                      <div
                        className={cn(
                          "h-full",
                          status === "completed" ? "bg-blue-600" : "bg-gray-200"
                        )}
                      />
                    </div>
                  )}

                  {/* Step Circle */}
                  <div className="relative flex items-center justify-center">
                    <div
                      className={cn(
                        "flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200",
                        status === "completed"
                          ? "border-blue-600 bg-blue-600 text-white shadow-sm"
                          : status === "current"
                          ? "border-blue-600 bg-blue-50 text-blue-600 shadow-lg ring-4 ring-blue-100"
                          : "border-gray-300 bg-white text-gray-400"
                      )}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>

                  {/* Step Label */}
                  <div className="mt-3 text-center">
                    <div
                      className={cn(
                        "text-sm font-medium transition-colors duration-200",
                        status === "current"
                          ? "text-blue-600 font-semibold"
                          : status === "completed"
                          ? "text-gray-900"
                          : "text-gray-500"
                      )}
                    >
                      {step.name}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </nav>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {renderStepContent()}
      </div>
    </div>
  );
};
